/**
 * The rules of the cloud sync — pure, and the only place they are stated.
 *
 * The application stays offline-first: Dexie is what a screen reads, and a
 * phone with no signal keeps working exactly as it did. The cloud is a *mirror*
 * laid over that, so a status one commander taps on their phone is on the מ"פ's
 * screen seconds later without either of them reloading anything.
 *
 * Two directions, one cursor each:
 *
 * - **Push.** Every record whose `updatedAt` moved past the last push cursor
 *   goes up. The cursor is a timestamp, not a flag on the row, so a record that
 *   was written while the device was offline is picked up by the next pass with
 *   nothing to remember about it.
 * - **Pull.** The server hands back everything stamped after the pull cursor.
 *   Each incoming record is merged against the local one by {@link mergeRecord}.
 *
 * Conflicts are settled last-write-wins on `updatedAt`, with the record id
 * breaking an exact tie so two devices that write in the same millisecond still
 * converge on the same answer rather than flapping between two.
 *
 * The append-only ledger is a special case and a simpler one: a row is never
 * edited, so an id that is already held is simply not written again.
 *
 * Nothing here touches Dexie, the network or the clock beyond what it is
 * handed — `src/db/sync-store.ts` does all three, and the healthcheck exercises
 * the decisions in this module directly.
 */
import type { StoreDomain } from './store-events';

/** The tables that ride the mirror, in the order a push sends them. */
export const SYNC_TABLES = [
  'vehicles',
  'soldiers',
  'tasks',
  'taskTeams',
  'taskCrew',
  // The quick mission log. It rides the mirror like any other record — the
  // server stores the payload verbatim — and is *also* projected into a table of
  // its own by `functions/api/sync/push.js`, so the log stays queryable from D1.
  'quickDriverTasks',
  // אנשי חוץ. A contractor kept on one manager's phone is a contractor the whole
  // unit should not have to type again, so the directory rides the mirror too.
  'externalPeople',
] as const;

export type SyncTable = (typeof SYNC_TABLES)[number];

/**
 * Which part of the reactive store a mirrored table belongs to.
 *
 * A pull writes straight into Dexie, which no screen is watching — so the pass
 * has to announce what landed, or a record another operator wrote would sit in
 * the database until something else happened to make a screen re-read. This is
 * the map that turns "these tables changed" into the announcements
 * `src/core/store-events.ts` carries.
 */
export const SYNC_TABLE_DOMAINS: Record<SyncTable, StoreDomain> = {
  vehicles: 'fleet',
  soldiers: 'fleet',
  tasks: 'tasks',
  taskTeams: 'assignments',
  taskCrew: 'assignments',
  // A logged journey is read by the dashboard beside the board it is added to.
  quickDriverTasks: 'tasks',
  // The directory is read by the assignment form, which the convoy screens own.
  externalPeople: 'assignments',
};

/**
 * The announcements one pull owes the screens: one per domain that actually
 * received a row, in a stable order and never repeated. A pass that wrote
 * nothing announces nothing — repainting every screen on an empty poll would
 * fight with whatever the operator is typing.
 */
export function pulledDomains(
  tables: readonly SyncTable[],
  auditWritten: boolean = false,
): StoreDomain[] {
  const domains = tables.map((table) => SYNC_TABLE_DOMAINS[table]).filter(Boolean);
  if (auditWritten) domains.push('audit');
  return [...new Set(domains)];
}

/**
 * How often a signed-in device asks the cloud what changed.
 *
 * The mandated window is 5–10 seconds and this sits at the fast end of it: the
 * real-time mandate is about a status tapped on one phone appearing on the מ"פ's
 * without either of them reloading, and the poll is the worst case for that. It
 * is only ever the worst case — the device that made the change pushes it within
 * {@link SYNC_POLL_MS} of nothing at all (the write signal fires immediately),
 * and another window on the same device hears it on the same frame through
 * `./live-updates`. Five seconds is what is left for the hop between two phones.
 *
 * A roster of twenty phones at five seconds is four requests a second against a
 * D1 read — not a load worth trading the latency for.
 */
export const SYNC_POLL_MS = 5_000;

/** The longest a failing device waits before trying again. */
export const SYNC_MAX_BACKOFF_MS = 120_000;

/** The moment before any record existed — the cursor a fresh device starts at. */
export const SYNC_EPOCH = '1970-01-01T00:00:00.000Z';

/** Where a device is in its conversation with the cloud. */
export interface SyncCursor {
  /** Everything stamped at or before this has already been pulled. */
  pulledThrough: string;
  /** Everything stamped at or before this has already been pushed. */
  pushedThrough: string;
  /**
   * Ledger rows run on their own pair of cursors: they carry no `updatedAt`, so
   * they are ordered by `createdAt`, and mixing the two clocks on one cursor
   * would let a busy entity table drag the ledger's position past rows that had
   * not been sent yet.
   */
  auditPulledThrough: string;
  auditPushedThrough: string;
}

export const EMPTY_CURSOR: SyncCursor = {
  pulledThrough: SYNC_EPOCH,
  pushedThrough: SYNC_EPOCH,
  auditPulledThrough: SYNC_EPOCH,
  auditPushedThrough: SYNC_EPOCH,
};

/** What every synced record must carry for the mirror to reason about it. */
export interface SyncableRecord {
  id: string;
  updatedAt?: string;
  createdAt?: string;
}

/** How the shell reports the mirror to the operator. */
export type SyncState = 'offline' | 'idle' | 'syncing' | 'error';

export const SYNC_STATE_LABELS: Record<SyncState, string> = {
  offline: 'עבודה מקומית',
  idle: 'מסונכרן',
  syncing: 'מסנכרן…',
  error: 'סנכרון נכשל',
};

/**
 * The stamp a record is ordered by. `updatedAt` is what a write moves;
 * `createdAt` covers a row that is only ever appended, and the epoch covers a
 * record so malformed it carries neither — such a record sorts first and is
 * therefore never mistaken for the newest version of anything.
 */
export const stampOf = (record: SyncableRecord | null | undefined): string =>
  record?.updatedAt || record?.createdAt || SYNC_EPOCH;

/**
 * Which version of a record wins.
 *
 * Newer `updatedAt` takes it. An exact tie is broken on the id — arbitrary, but
 * *stable*, which is the property that matters: both devices run this same
 * comparison and both reach the same verdict, so the pair converges instead of
 * overwriting each other on every pass.
 */
export function mergeRecord<T extends SyncableRecord>(local: T | null | undefined, remote: T): T {
  if (!local) return remote;
  const here = stampOf(local);
  const there = stampOf(remote);
  if (there > here) return remote;
  if (there < here) return local;
  return remote.id > local.id ? remote : local;
}

/** True when the incoming version is the one that should be written. */
export const shouldApplyRemote = <T extends SyncableRecord>(
  local: T | null | undefined,
  remote: T,
): boolean => mergeRecord(local, remote) === remote && (!local || stampOf(remote) >= stampOf(local));

/** Everything stamped after the cursor — what one push has to carry. */
export function pendingPush<T extends SyncableRecord>(
  records: readonly T[],
  since: string,
): T[] {
  return records.filter((record) => stampOf(record) > since).sort((a, b) => stampOf(a).localeCompare(stampOf(b)));
}

/**
 * The cursor to keep once a batch has been dealt with: the newest stamp in it,
 * or the cursor unchanged when the batch was empty.
 *
 * Never advanced past what was actually seen — a cursor moved to "now" would
 * skip a record written by another device a moment earlier and still in flight.
 */
export function advanceCursor(current: string, records: readonly SyncableRecord[]): string {
  return records.reduce((newest, record) => {
    const stamp = stampOf(record);
    return stamp > newest ? stamp : newest;
  }, current);
}

/**
 * How long to wait after a failure — 8s, 16s, 32s… up to two minutes, so a
 * device that is simply out of coverage stops hammering a radio that has
 * nothing to talk to. A success resets it by passing `0`.
 */
export function backoffDelay(consecutiveFailures: number, base: number = SYNC_POLL_MS): number {
  if (consecutiveFailures <= 0) return base;
  return Math.min(base * 2 ** consecutiveFailures, SYNC_MAX_BACKOFF_MS);
}

/**
 * Where the API lives.
 *
 * An explicit `VITE_API_BASE_URL` wins — that is a device pointed at another
 * deployment. With none set the mirror talks to `/api` on the origin it was
 * served from, which is exactly where the Pages Functions in `functions/api/`
 * answer. A dev server with no backend behind it simply gets a 404, the state
 * falls to `offline`, and the app carries on against Dexie alone.
 */
export function syncEndpoint(baseUrl: string | null | undefined, path: string): string {
  const base = String(baseUrl ?? '').trim().replace(/\/+$/, '') || '/api';
  return `${base}/${path.replace(/^\/+/, '')}`;
}

/** Ledger rows the device does not already hold, oldest first. */
export function unseenAuditRows<T extends { id: string; createdAt: string }>(
  rows: readonly T[],
  held: ReadonlySet<string>,
): T[] {
  return rows
    .filter((row) => !held.has(row.id))
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}
